import React, { useState, useEffect, ReactElement } from "react";
import { FiEdit2, FiCheck, FiX } from "react-icons/fi";

interface EditableFieldProps {
  id: string;
  value: string;
  multiline?: boolean;
  editable?: boolean;
  placeholder?: string;
  onSave?: (value: string) => Promise<void>;
}

const EditableField: React.FC<EditableFieldProps> = ({
  id,
  value,
  multiline = false,
  editable = true,
  placeholder,
  onSave,
}) => {
  const [tempValue, setTempValue] = useState(value);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const handleSave = async () => {
    if (onSave) {
      if (tempValue === value) {
        setEditing(false);
        return;
      }
      try {
        setSaving(true);
        await onSave(tempValue);
        setError(null);
        setEditing(false);
      } catch (err) {
        setError("Failed to save.");
      } finally {
        setSaving(false);
      }
    }
  };

  const handleCancel = () => {
    setTempValue(value);
    setEditing(false);
    setError(null);
  };

  const TooltipButton = ({
    onClick,
    icon,
    tooltip,
    colorClass,
    disabled,
  }: {
    onClick: () => void;
    icon: React.ReactElement;
    tooltip: string;
    colorClass: string;
    disabled: boolean;
  }) => (
    <div className="relative group">
      <button
        type="button"
        onClick={onClick}
        className={`${colorClass} hover:opacity-90`}
        disabled={disabled}
      >
        {icon}
      </button>
      <span className="absolute -top-8 right-1 scale-0 group-hover:scale-100 transition-transform bg-black text-white text-xs px-2 py-1 rounded shadow-md whitespace-nowrap z-10">
        {tooltip}
      </span>
    </div>
  );

  return (
    <div className="relative">
      {editing ? (
        <>
          {multiline ? (
            <textarea
              id={id}
              className="bg-transparent border-0 border-b border-primaryAlt box-border text-white block text-base mb-[30px] outline-none opacity-60 pb-[15px] pl-[10px] w-full resize-none"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              rows={3}
              autoFocus
              placeholder={placeholder}
              disabled={saving}
            />
          ) : (
            <input
              id={id}
              className="bg-transparent border-0 border-b border-primaryAlt box-border text-white block text-base mb-[30px] outline-none opacity-60 pb-[15px] pl-[10px] w-full"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              autoFocus
              placeholder={placeholder}
              disabled={saving}
            />
          )}

          <div className="absolute right-2 top-2 flex gap-2">
            <TooltipButton
              onClick={handleCancel}
              icon={<FiX size={16} />}
              tooltip="Cancel"
              colorClass="text-red-400"
              disabled={saving}
            />
            <TooltipButton
              onClick={handleSave}
              icon={<FiCheck size={16} />}
              tooltip="Confirm"
              colorClass="text-green-400"
              disabled={saving}
            />
          </div>

          {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </>
      ) : (
        <>
          <div className="bg-transparent border-0 border-b border-white box-border text-white block text-base mb-[30px] outline-none opacity-60 pb-[15px] pl-[10px] w-full break-words whitespace-pre-wrap">
            {tempValue!=""?tempValue:placeholder}
          </div>
          {editable && (
            <div className="absolute right-2 top-2 group">
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-primary hover:text-white"
              >
                <FiEdit2 size={16} />
              </button>
              <span className="absolute -top-8 right-1 scale-0 group-hover:scale-100 transition-transform bg-black text-white text-xs px-2 py-1 rounded shadow-md whitespace-nowrap z-10">
                Edit
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EditableField;
